/**
 * ResumeUploadCard — dropzone + upload action + status, driven by useUploadResume.
 * Reused on the intake success panel and on the candidate detail page.
 * Note: PDFs are stored but not text-indexed in Phase 1 (text_indexed:false).
 */

import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Info } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ResumeDropzone } from './ResumeDropzone';
import { InlineError } from './states';
import { useUploadResume } from '../hooks/useCandidates';
import { getRecruitmentError } from '../utils/apiError';

interface ResumeUploadCardProps {
	candidateId: number;
	initialFile?: File | null;
	/** Upload `initialFile` automatically once when the card mounts. */
	autoUpload?: boolean;
	onUploaded?: (res: { resume_id: number; text_indexed: boolean }) => void;
	className?: string;
}

export const ResumeUploadCard: React.FC<ResumeUploadCardProps> = ({
	candidateId,
	initialFile = null,
	autoUpload = false,
	onUploaded,
	className,
}) => {
	const [file, setFile] = useState<File | null>(initialFile);
	const [result, setResult] = useState<{ resume_id: number; text_indexed: boolean } | null>(null);
	const [error, setError] = useState<string | null>(null);
	const upload = useUploadResume();
	const autoDone = useRef(false);

	const doUpload = async (f: File) => {
		setError(null);
		try {
			const res = await upload.mutateAsync({ candidateId, file: f });
			setResult(res);
			setFile(null);
			onUploaded?.(res);
		} catch (e) {
			setError(getRecruitmentError(e).message);
		}
	};

	// Auto-upload a pre-selected file once (e.g. the resume dropped during intake).
	useEffect(() => {
		if (autoUpload && initialFile && !autoDone.current) {
			autoDone.current = true;
			void doUpload(initialFile);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoUpload, initialFile]);

	if (result) {
		return (
			<div className={className}>
				<div className='flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800'>
					<CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0' />
					<div>
						<p className='font-medium'>Resume uploaded.</p>
						{!result.text_indexed && (
							<p className='mt-0.5 flex items-center gap-1 text-xs text-green-700'>
								<Info className='h-3 w-3' /> PDF text is not indexed yet — this resume will appear in Resume Search later.
							</p>
						)}
					</div>
				</div>
				<div className='mt-2'>
					<Button variant='ghost' size='sm' onClick={() => setResult(null)}>
						Upload another
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className={className}>
			<ResumeDropzone file={file} onFile={setFile} disabled={upload.isPending} />
			{error && <InlineError className='mt-2' message={error} />}
			<div className='mt-3'>
				<Button variant='primary' size='sm' disabled={!file || upload.isPending} loading={upload.isPending} onClick={() => file && doUpload(file)}>
					Upload resume
				</Button>
			</div>
		</div>
	);
};
